import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { COPY } from "../constants";
import { rv } from "../tokens";
import { CheckIcon, ClipboardCheckIcon, ClockIcon } from "../ui/icons";
import StatusPill from "../ui/StatusPill";

const useStyles = makeStyles((theme) => ({
  wrap: {
    maxWidth: 620,
    margin: "0 auto",
    padding: theme.spacing(6, 2),
    textAlign: "center",
  },
  checkRing: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    background: rv.verifiedBg,
    border: `2px solid ${rv.verifiedBorder}`,
    color: rv.verifiedFg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
  },
  title: {
    fontWeight: 700,
    fontSize: 24,
    color: rv.heading,
    margin: theme.spacing(2.5, 0, 1),
  },
  body: {
    color: rv.muted,
    fontSize: 14.5,
    lineHeight: 1.6,
    maxWidth: 480,
    margin: "0 auto",
  },
  pill: {
    marginTop: theme.spacing(2),
  },
  timeline: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: 0,
    margin: theme.spacing(4, 0),
  },
  node: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    width: 96,
  },
  dot: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },
  dotDone: { background: rv.verifiedBg, color: rv.verifiedFg, border: `2px solid ${rv.verifiedBorder}` },
  dotActive: { background: rv.reviewBg, color: rv.reviewFg, border: `2px solid ${rv.reviewBorder}` },
  dotUpcoming: { background: "#fff", color: rv.disabled, border: `2px dashed ${rv.hairline}` },
  nodeLabel: { fontSize: 12.5, fontWeight: 600, color: rv.body },
  nodeLabelDim: { color: rv.muted3, fontWeight: 500 },
  connector: { height: 2, flex: "0 0 36px", marginTop: 17, background: rv.verifiedBorder },
  connectorDim: { height: 0, borderTop: `2px dashed ${rv.hairline}`, flex: "0 0 36px", marginTop: 17 },
  actions: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  primary: {
    background: rv.gold,
    color: rv.onDark,
    minHeight: 44,
    minWidth: 180,
    borderRadius: rv.radiusInput,
    boxShadow: "none",
    textTransform: "none",
    fontWeight: 700,
    "&:hover": { background: rv.goldHover },
  },
  secondary: {
    color: rv.green,
    borderColor: rv.green,
    minHeight: 44,
    borderRadius: rv.radiusInput,
    textTransform: "none",
    fontWeight: 600,
  },
}));

interface Props {
  isTier2: boolean;
  onReturnDashboard: () => void;
  onUpgrade: () => void;
}

export default function Submitted({ isTier2, onReturnDashboard, onUpgrade }: Props) {
  const classes = useStyles();
  return (
    <div className={classes.wrap}>
      <div className={classes.checkRing}>
        <CheckIcon size={38} />
      </div>
      <h1 className={classes.title}>{COPY.submitted.title}</h1>
      <p className={classes.body}>{COPY.submitted.body}</p>
      <div className={classes.pill}>
        <StatusPill tone="review" icon={<ClockIcon size={12} />}>
          {COPY.submitted.pill}
        </StatusPill>
      </div>

      <div className={classes.timeline}>
        <div className={classes.node}>
          <span className={`${classes.dot} ${classes.dotDone}`}>
            <CheckIcon size={16} />
          </span>
          <span className={classes.nodeLabel}>Submitted</span>
        </div>
        <span className={classes.connector} />
        <div className={classes.node}>
          <span className={`${classes.dot} ${classes.dotActive}`}>
            <ClockIcon size={16} />
          </span>
          <span className={classes.nodeLabel}>Under review</span>
        </div>
        <span className={classes.connectorDim} />
        <div className={classes.node}>
          <span className={`${classes.dot} ${classes.dotUpcoming}`}>
            <ClipboardCheckIcon size={16} />
          </span>
          <span className={`${classes.nodeLabel} ${classes.nodeLabelDim}`}>
            Approved
          </span>
        </div>
      </div>

      <div className={classes.actions}>
        {isTier2 && (
          <Button variant="outlined" className={classes.secondary} onClick={onUpgrade}>
            {COPY.submitted.tier2Secondary}
          </Button>
        )}
        <Button variant="contained" className={classes.primary} onClick={onReturnDashboard}>
          {COPY.submitted.primary}
        </Button>
      </div>
    </div>
  );
}
